import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type ButtonHTMLAttributes } from 'react'

import { cn } from '@/lib/utils'

import { Spinner } from './spinner'

export const buttonVariants = cva(
  'relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-[9px] font-medium whitespace-nowrap transition-[background-color,border-color,color,box-shadow,transform] duration-150 select-none active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-fg shadow-[inset_0_1px_0_rgb(255_255_255/0.16),0_1px_2px_rgb(16_27_51/0.18)] hover:bg-primary-hover',
        secondary:
          'border border-line bg-surface text-ink shadow-[0_1px_1px_rgb(16_27_51/0.04)] hover:border-line-strong hover:bg-surface-2',
        ghost: 'text-ink-2 hover:bg-surface-3 hover:text-ink',
        danger: 'bg-danger text-white hover:brightness-95',
        'danger-ghost': 'text-danger-ink hover:bg-danger-soft',
        link: 'h-auto px-0 text-accent-ink underline-offset-4 hover:underline active:scale-100',
      },
      size: {
        sm: 'h-8 px-3 text-[0.8125rem] [&_svg]:size-4',
        md: 'h-9 px-3.5 text-[0.8125rem] [&_svg]:size-4',
        lg: 'h-11 px-5 text-sm [&_svg]:size-[18px]',
        icon: 'size-9 [&_svg]:size-[18px]',
        'icon-sm': 'size-8 [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, loading, disabled, children, type, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props}>
          {children}
        </Slot>
      )
    }
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && <Spinner className="absolute" />}
        <span className={cn('inline-flex items-center gap-2', loading && 'invisible')}>{children}</span>
      </button>
    )
  },
)
Button.displayName = 'Button'
